import React, { useState, useEffect, useCallback } from "react";
import { api, eur } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import MonthSelector from "../components/MonthSelector";
import InvoiceForm, { newInvoice } from "../components/InvoiceForm";
import { Plus, Search, Pencil, Trash2, FileText, ScanLine } from "lucide-react";
import { useNavigate } from "react-router-dom";

export default function Acquisti() {
  const { canWrite } = useAuth();
  const [month, setMonth] = useState(6);
  const [q, setQ] = useState("");
  const [list, setList] = useState([]);
  const [editing, setEditing] = useState(null);
  const navigate = useNavigate();

  const load = useCallback(() => {
    const params = { month: `2026-${String(month).padStart(2, "0")}` };
    if (q) params.q = q;
    api.get("/invoices", { params }).then((r) => setList(r.data)).catch(() => {});
  }, [month, q]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const h = () => load();
    window.addEventListener("data-synced", h);
    return () => window.removeEventListener("data-synced", h);
  }, [load]);

  const remove = async (id) => {
    if (!window.confirm("Eliminare questa fattura?")) return;
    await api.delete(`/invoices/${id}`);
    load();
  };

  const total = list.reduce((s, i) => s + (i.total_eur || 0), 0);

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-head text-3xl font-bold tracking-tight text-navy">Acquisti</h1>
          <p className="text-slate-500">Fatture e scontrini · <span className="tnum font-semibold text-navy">{eur(total)}</span></p>
        </div>
        {canWrite && (
          <div className="flex gap-2">
            <button data-testid="scan-cta" onClick={() => navigate("/scan")} className="flex h-14 items-center gap-2 rounded-xl border-2 border-gold px-5 font-semibold text-navy hover:bg-gold/10">
              <ScanLine className="h-5 w-5" /> Scansiona
            </button>
            <button data-testid="add-invoice-btn" onClick={() => setEditing(newInvoice())} className="flex h-14 items-center gap-2 rounded-xl bg-navy px-5 font-semibold text-white hover:bg-navy-hover">
              <Plus className="h-5 w-5" /> Manuale
            </button>
          </div>
        )}
      </div>

      <MonthSelector month={month} onChange={setMonth} />

      <div className="relative">
        <Search className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" />
        <input data-testid="search-invoices" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca fornitore o prodotto…" className="h-12 w-full rounded-lg border-2 border-slate-200 pl-11 pr-4 outline-none focus:border-gold" />
      </div>

      {list.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-300 bg-white py-16 text-center">
          <FileText className="mx-auto mb-3 h-10 w-10 text-slate-300" />
          <p className="text-slate-400">Nessuna fattura per questo mese</p>
        </div>
      ) : (
        <>
          {/* Desktop grid */}
          <div className="hidden overflow-hidden rounded-xl border border-slate-200 bg-white md:block">
            <table className="w-full text-sm">
              <thead className="bg-navy text-white">
                <tr>
                  <th className="p-3 text-left font-semibold">Data</th>
                  <th className="p-3 text-left font-semibold">Fornitore</th>
                  <th className="p-3 text-left font-semibold">Righe</th>
                  <th className="p-3 text-right font-semibold">Totale (EUR)</th>
                  <th className="p-3"></th>
                </tr>
              </thead>
              <tbody>
                {list.map((inv, i) => (
                  <tr key={inv.id} data-testid={`invoice-row-${inv.id}`} className={i % 2 ? "bg-slate-50" : "bg-white"}>
                    <td className="tnum p-3">{inv.date}</td>
                    <td className="p-3 font-medium text-navy">
                      {inv.supplier_name}
                      {inv.is_credit_note && <span className="ml-2 rounded bg-red-100 px-1.5 py-0.5 text-xs text-red-600">Nota credito</span>}
                      {inv.currency !== "EUR" && <span className="ml-2 rounded bg-blue-100 px-1.5 py-0.5 text-xs text-blue-600">{inv.currency}</span>}
                    </td>
                    <td className="p-3 text-slate-500">{inv.line_items?.length || 0} prodotti</td>
                    <td className={`tnum p-3 text-right font-semibold ${inv.total_eur < 0 ? "text-red-600" : "text-navy"}`}>{eur(inv.total_eur)}</td>
                    <td className="p-3">
                      {canWrite && (
                        <div className="flex justify-end gap-1">
                          <button onClick={() => setEditing(inv)} className="rounded p-1.5 text-slate-400 hover:bg-slate-100"><Pencil className="h-4 w-4" /></button>
                          <button data-testid={`delete-invoice-${inv.id}`} onClick={() => remove(inv.id)} className="rounded p-1.5 text-red-400 hover:bg-red-50"><Trash2 className="h-4 w-4" /></button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="space-y-3 md:hidden">
            {list.map((inv) => (
              <div key={inv.id} data-testid={`invoice-card-${inv.id}`} className="rounded-xl border border-slate-200 bg-white p-4">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="font-head font-semibold text-navy">{inv.supplier_name}</div>
                    <div className="tnum text-sm text-slate-500">{inv.date} · {inv.line_items?.length || 0} prodotti</div>
                  </div>
                  <div className={`tnum font-head text-lg font-bold ${inv.total_eur < 0 ? "text-red-600" : "text-navy"}`}>{eur(inv.total_eur)}</div>
                </div>
                {canWrite && (
                  <div className="mt-3 flex gap-2 border-t border-slate-100 pt-3">
                    <button onClick={() => setEditing(inv)} className="flex flex-1 items-center justify-center gap-1 rounded-lg bg-slate-100 py-2 text-sm font-medium"><Pencil className="h-4 w-4" /> Modifica</button>
                    <button onClick={() => remove(inv.id)} className="flex items-center justify-center rounded-lg bg-red-50 px-4 text-red-500"><Trash2 className="h-4 w-4" /></button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </>
      )}

      {editing && <InvoiceForm initial={editing} onClose={() => setEditing(null)} onSaved={load} />}
    </div>
  );
}
